
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/components/ui/use-toast";

export const useFileUpload = () => {
  const { toast } = useToast();
  
  const ensureBucketExists = async (bucketName: string): Promise<boolean> => {
    try {
      // First check if the bucket exists
      const { data: buckets, error: listError } = await supabase.storage.listBuckets();
      
      if (listError) {
        console.error("Error listing buckets:", listError);
        return false;
      }
      
      // Check if our bucket exists in the list
      const bucketExists = buckets?.some(b => b.name === bucketName);
      
      if (bucketExists) {
        console.log(`Bucket ${bucketName} already exists`);
        return true;
      }
      
      // Bucket doesn't exist - but we won't try to create it automatically
      // as this may be causing RLS policy violations
      console.log(`Bucket ${bucketName} does not exist - using it anyway`);
      return true;
    } catch (error) {
      console.error(`Error in ensureBucketExists for ${bucketName}:`, error);
      return false;
    }
  };
  
  const uploadFile = async (
    file: File, 
    bucketName: string, 
    customPath?: string
  ): Promise<string | null> => {
    try {
      console.log(`Uploading file ${file.name} to bucket ${bucketName}`);
      
      // Use the provided bucket name (defaults to team-chat-images for backwards compatibility)
      const finalBucketName = bucketName || 'team-chat-images';
      
      // Verify bucket exists (but don't try to create it)
      const bucketReady = await ensureBucketExists(finalBucketName);
      
      if (!bucketReady) {
        toast({
          variant: "destructive",
          title: "Storage Error",
          description: `Failed to access storage bucket: ${finalBucketName}`
        });
        return null;
      }
      
      // Generate file path with UUID
      const fileExt = file.name.split('.').pop();
      const filePath = customPath || `${crypto.randomUUID()}.${fileExt}`;
      
      // Upload file
      const { error: uploadError } = await supabase.storage
        .from(finalBucketName)
        .upload(filePath, file);
        
      if (uploadError) {
        console.error("Error uploading file:", uploadError);
        toast({
          variant: "destructive",
          title: "Upload Failed",
          description: "Could not upload the file. Please try again."
        });
        return null;
      }
      
      // Get public URL - use the correct format that will work with our app
      const { data: { publicUrl } } = supabase.storage
        .from(finalBucketName)
        .getPublicUrl(filePath);
        
      console.log("File uploaded successfully, public URL:", publicUrl);
      
      return publicUrl;
    } catch (error) {
      console.error("Error in uploadFile:", error);
      toast({
        variant: "destructive",
        title: "Upload Error",
        description: "An unexpected error occurred during file upload"
      });
      return null;
    }
  };
  
  return {
    uploadFile,
    ensureBucketExists
  };
};

export default useFileUpload;
